import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, LogOut, Package } from "lucide-react";
import { toast } from "sonner";
import { customerApi, apiError, useCustomer } from "../context/CustomerContext";
import { AuthPanel } from "../components/account/AuthPanel";
import { ProfileFields, checkProfile, PROFILE } from "../components/account/ProfileFields";
import { eur } from "../lib/api";

const pick = (u) => Object.fromEntries(PROFILE.map(([k]) => [k, u?.[k] || ""]));

const MyOrders = () => {
  const [orders, setOrders] = useState(null);
  useEffect(() => { customerApi.orders().then((d) => setOrders(Array.isArray(d) ? d : [])).catch(() => setOrders([])); return undefined; }, []);
  return (
    <section data-testid="my-orders" className="mt-10">
      <h2 className="font-serif-d font-black text-2xl mb-3 flex items-center gap-2"><Package className="w-5 h-5 text-[#8CD05A]" /> Mis pedidos</h2>
      {orders === null ? <Loader2 className="w-5 h-5 animate-spin text-[#8CD05A]" /> : orders.length === 0 ? (
        <p data-testid="my-orders-empty" className="text-sm text-[#C9D6C2]">Aún no tienes pedidos con esta cuenta.</p>
      ) : (
        <ul className="space-y-2">
          {orders.map((o) => (
            <li key={o.session_id} className="flex items-center justify-between gap-3 rounded-[10px] border border-[#8CD05A]/20 bg-[#0E2A1C] px-4 py-3 text-sm">
              <span><span className="font-mono-d">{o.session_id.toUpperCase()}</span><br /><span className="text-xs text-[#C9D6C2]">{new Date(o.created_at).toLocaleDateString("es-ES")}</span></span>
              <span className="text-right"><span className="font-mono-d">{eur(o.amount || 0)}</span><br />
                <span className={`text-xs ${o.payment_status === "paid" ? "text-[#B9E79A]" : "text-[#F2B441]"}`}>{o.shipped_at ? "Enviado" : o.payment_status === "paid" ? "Pagado" : "Pendiente de transferencia"}</span>
                {o.tracking && <><br /><span className="text-xs text-[#C9D6C2]">{o.tracking.carrier} · {o.tracking.url ? <a href={o.tracking.url} target="_blank" rel="noreferrer" className="underline">{o.tracking.number}</a> : o.tracking.number}</span></>}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

const Profile = ({ user }) => {
  const { setUser, logout } = useCustomer();
  const [v, setV] = useState(() => pick(user));
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    const ex = checkProfile(v, false);
    setErrors(ex);
    if (Object.keys(ex).length) return;
    setBusy(true);
    try { setUser(await customerApi.update(v)); toast.success("Datos guardados"); } catch (x) { toast.error(apiError(x)); }
    setBusy(false);
  };
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-[#C9D6C2]">Conectado como <strong data-testid="account-email-display" className="text-[#F6EEDC]">{user.email}</strong></p>
        <button type="button" onClick={logout} data-testid="account-logout" className="h-10 px-4 rounded-full border border-[#F6EEDC]/25 text-sm inline-flex items-center gap-2 hover:border-[#F2B441]"><LogOut className="w-4 h-4" /> Cerrar sesión</button>
      </div>
      <form onSubmit={save} noValidate data-testid="profile-form" className="mt-6 space-y-4">
        <h2 className="font-serif-d font-black text-2xl">Mis datos</h2>
        <ProfileFields value={v} onChange={setV} errors={errors} />
        <button type="submit" disabled={busy} data-testid="profile-save" className="h-12 px-7 rounded-full bg-[#8CD05A] text-[#0E2A1C] font-semibold hover:bg-[#F2B441] transition-colors disabled:opacity-50 inline-flex items-center gap-2">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Guardar cambios
        </button>
      </form>
      <MyOrders />
    </>
  );
};

export default function Account() {
  const { user } = useCustomer();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const next = params.get("volver");
  useEffect(() => { if (user && next === "compra") nav("/finalizar-compra"); }, [user, next, nav]);
  return (
    <main data-testid="account-page" className="max-w-[640px] mx-auto px-5 pt-28 pb-24">
      <h1 className="font-serif-d font-black text-4xl sm:text-5xl mb-8">{user ? "Mi cuenta" : "Iniciar sesión"}</h1>
      {user === null ? <Loader2 className="w-6 h-6 animate-spin text-[#8CD05A]" /> : user ? <Profile user={user} /> : <AuthPanel />}
      {!user && <p className="mt-8 text-sm text-[#C9D6C2]">También puedes <Link to="/finalizar-compra" className="underline underline-offset-4">comprar sin cuenta</Link>.</p>}
    </main>
  );
}
